// pages/Dashboard.jsx (Admin Portal)
import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ============ DESIGN TOKENS ============
const tokens = {
  colors: {
    primary: {
      50: '#fdf4ff',
      100: '#fae8ff',
      500: '#d946ef',
      600: '#c026d3',
      700: '#a21caf',
    },
    neutral: {
      0: '#ffffff',
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
    },
    success: { light: '#ecfdf5', main: '#10b981', dark: '#059669' },
    warning: { light: '#fffbeb', main: '#f59e0b', dark: '#d97706' },
    error: { light: '#fef2f2', main: '#ef4444', dark: '#dc2626' },
    accent: {
      purple: '#8b5cf6',
      pink: '#ec4899',
      indigo: '#6366f1',
      cyan: '#06b6d4',
      emerald: '#10b981',
      amber: '#f59e0b',
    },
  },
  shadows: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
  },
  transitions: {
    fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
    normal: '200ms cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '300ms cubic-bezier(0.4, 0, 0.2, 1)',
  },
  radius: {
    sm: '6px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    '2xl': '24px',
    full: '9999px',
  },
};

// ============ ANIMATIONS ============
const fadeInUp = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(20px); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0); 
  }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// ============ STYLED COMPONENTS ============
const PageWrapper = styled.div`
  min-height: 100%;
  background: ${tokens.colors.neutral[50]};
  padding: 32px;

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 32px;
  animation: ${fadeInUp} 0.5s ease-out;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${tokens.colors.neutral[900]};
  margin: 0 0 8px 0;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 0.95rem;
  color: ${tokens.colors.neutral[500]};
  margin: 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-bottom: 32px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const StatCard = styled.div`
  background: ${tokens.colors.neutral[0]};
  border-radius: ${tokens.radius.xl};
  padding: 24px;
  box-shadow: ${tokens.shadows.sm};
  border: 1px solid ${tokens.colors.neutral[200]};
  animation: ${fadeInUp} 0.5s ease-out ${props => props.$delay} backwards;
  transition: all ${tokens.transitions.normal};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.$gradient};
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${tokens.shadows.lg};
  }

  &.alert {
    border-left: 4px solid ${tokens.colors.warning.main};
    
    &::before {
      display: none;
    }
  }
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  background: ${props => props.$bg};
  border-radius: ${tokens.radius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  font-size: 1.5rem;
`;

const StatLabel = styled.h3`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${tokens.colors.neutral[500]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 8px 0;
`;

const StatValue = styled.div`
  font-size: 2.25rem;
  font-weight: 800;
  color: ${tokens.colors.neutral[900]};
  line-height: 1;
  margin-bottom: 12px;
`;

const StatLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${tokens.colors.accent.purple};
  text-decoration: none;
  transition: color ${tokens.transitions.fast};

  &:hover {
    color: ${tokens.colors.accent.pink};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${tokens.colors.neutral[0]};
  border-radius: ${tokens.radius.xl};
  box-shadow: ${tokens.shadows.sm};
  border: 1px solid ${tokens.colors.neutral[200]};
  animation: ${fadeInUp} 0.5s ease-out 0.3s backwards;
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid ${tokens.colors.neutral[100]};
  display: flex;
  align-items: center;
  justify-content: space-between;

  h2 {
    font-size: 1rem;
    font-weight: 700;
    color: ${tokens.colors.neutral[800]};
    margin: 0;
    display: flex;
    align-items: center;
    gap: 10px;
  }
`;

const ViewAllLink = styled(Link)`
  font-size: 0.8rem;
  color: ${tokens.colors.accent.purple};
  font-weight: 600;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: color ${tokens.transitions.fast};

  &:hover {
    color: ${tokens.colors.accent.pink};
  }
`;

const CardBody = styled.div`
  padding: 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 14px 24px;
    text-align: left;
  }

  th {
    font-size: 0.7rem;
    font-weight: 600;
    color: ${tokens.colors.neutral[500]};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: ${tokens.colors.neutral[50]};
    border-bottom: 1px solid ${tokens.colors.neutral[200]};
  }

  tbody tr {
    border-bottom: 1px solid ${tokens.colors.neutral[100]};
    transition: background ${tokens.transitions.fast};

    &:hover {
      background: ${tokens.colors.neutral[50]};
    }

    &:last-child {
      border-bottom: none;
    }
  }

  td {
    font-size: 0.9rem;
    color: ${tokens.colors.neutral[700]};
  }

  @media (max-width: 640px) {
    th, td {
      padding: 12px 16px;
      font-size: 0.8rem;
    }
  }
`;

const UserCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  .name {
    font-weight: 600;
    color: ${tokens.colors.neutral[800]};
  }

  .email {
    font-size: 0.75rem;
    color: ${tokens.colors.neutral[500]};
  }
`;

const RiskBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: ${tokens.radius.full};
  font-size: 0.7rem;
  font-weight: 800;
  background: ${props => props.level === 'critical' ? tokens.colors.error.light : '#fff7ed'};
  color: ${props => props.level === 'critical' ? tokens.colors.error.dark : '#c2410c'};
  border: 1px solid ${props => props.level === 'critical' ? '#fecaca' : '#fed7aa'};
  text-transform: capitalize;
`;

const FollowUpList = styled.div`
  display: flex;
  flex-direction: column;
`;

const FollowUpItem = styled(Link)`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  padding: 16px 24px;
  text-decoration: none;
  border-bottom: 1px solid ${tokens.colors.neutral[100]};
  transition: background ${tokens.transitions.fast};

  &:hover { background: ${tokens.colors.neutral[50]}; }
  &:last-child { border-bottom: none; }

  .client { color: ${tokens.colors.neutral[800]}; font-weight: 700; margin-bottom: 4px; }
  .meta { color: ${tokens.colors.neutral[500]}; font-size: 0.78rem; }
  .reason { grid-column: 1 / -1; color: ${tokens.colors.neutral[600]}; font-size: 0.82rem; line-height: 1.45; }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: ${tokens.radius.full};
  font-size: 0.7rem;
  font-weight: 600;

  &.pending {
    background: ${tokens.colors.warning.light};
    color: ${tokens.colors.warning.dark};
  }

  &.approved {
    background: ${tokens.colors.success.light};
    color: ${tokens.colors.success.dark};
  }

  &.rejected {
    background: ${tokens.colors.error.light};
    color: ${tokens.colors.error.dark};
  }
`;

const EmptyState = styled.div`
  padding: 48px 24px;
  text-align: center;

  .icon {
    font-size: 2.5rem;
    margin-bottom: 12px;
    opacity: 0.5;
  }

  p {
    color: ${tokens.colors.neutral[500]};
    font-size: 0.9rem;
    margin: 0;
  }
`;

const LoaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
`;

const Loader = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid ${tokens.colors.neutral[200]};
  border-top-color: ${tokens.colors.accent.purple};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

// ============ ICON COMPONENTS ============
const ArrowRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

// ============ COMPONENT ============
const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentResults, setRecentResults] = useState([]);
  const [followUpCandidates, setFollowUpCandidates] = useState([]);
  const [pendingPractitioners, setPendingPractitioners] = useState([]);
  const [loading, setLoading] = useState(true);

  const getLicenseTypeDisplay = (practitioner) => {
    const licenseType = String(practitioner?.licenseType || '').trim();
    const licenseTypeOther = String(
      practitioner?.licenseTypeOther ||
      practitioner?.customLicenseType ||
      practitioner?.licenseTypeCustom ||
      ''
    ).trim();

    if (licenseType.toLowerCase() === 'other' && licenseTypeOther) {
      return licenseTypeOther;
    }

    return licenseType || 'N/A';
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const [dashboardRes, pendingRes] = await Promise.all([
        axios.get(`${API_URL}/admin/dashboard`),
        axios.get(`${API_URL}/admin/practitioners/pending`)
      ]);

      setStats(dashboardRes.data.stats);
      setRecentResults(dashboardRes.data.recentResults);
      setFollowUpCandidates(dashboardRes.data.followUpCandidates || []);
      setPendingPractitioners(pendingRes.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PageWrapper>
        <Container>
          <LoaderContainer>
            <Loader />
          </LoaderContainer>
        </Container>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Container>
        <Header>
          <Title>Admin Dashboard</Title>
          <Subtitle>Overview of your platform's performance and activity</Subtitle>
        </Header>

        <StatsGrid>
          <StatCard
            $delay="0s"
            $gradient={`linear-gradient(90deg, ${tokens.colors.warning.main}, ${tokens.colors.accent.amber})`}
            className={stats?.pendingApprovals > 0 ? 'alert' : ''}
          >
            <StatIcon $bg={tokens.colors.warning.light}>⏳</StatIcon>
            <StatLabel>Pending Approvals</StatLabel>
            <StatValue>{stats?.pendingApprovals || 0}</StatValue>
            <StatLink to="/practitioners?status=pending">
              Review applications <ArrowRightIcon />
            </StatLink>
          </StatCard>

          <StatCard
            $delay="0.1s"
            $gradient={`linear-gradient(90deg, ${tokens.colors.accent.purple}, ${tokens.colors.accent.pink})`}
          >
            <StatIcon $bg={`${tokens.colors.accent.purple}15`}>👥</StatIcon>
            <StatLabel>Total Practitioners</StatLabel>
            <StatValue>{stats?.totalPractitioners || 0}</StatValue>
            <StatLink to="/practitioners">
              View all <ArrowRightIcon />
            </StatLink>
          </StatCard>

          <StatCard
            $delay="0.2s"
            $gradient={`linear-gradient(90deg, ${tokens.colors.accent.cyan}, ${tokens.colors.accent.emerald})`}
          >
            <StatIcon $bg={`${tokens.colors.accent.cyan}15`}>📋</StatIcon>
            <StatLabel>Tests Completed</StatLabel>
            <StatValue>{stats?.totalTests || 0}</StatValue>
            <StatLink to="/results">
              View results <ArrowRightIcon />
            </StatLink>
          </StatCard>


          <StatCard
            $delay="0.25s"
            $gradient={`linear-gradient(90deg, ${tokens.colors.error.main}, ${tokens.colors.warning.main})`}
            className={stats?.alarmingResults > 0 ? 'alert' : ''}
          >
            <StatIcon $bg={tokens.colors.error.light}>🚩</StatIcon>
            <StatLabel>Follow-up Flags</StatLabel>
            <StatValue>{stats?.alarmingResults || 0}</StatValue>
            <StatLink to="/results">
              Review flagged results <ArrowRightIcon />
            </StatLink>
          </StatCard>

          <StatCard
            $delay="0.3s"
            $gradient={`linear-gradient(90deg, ${tokens.colors.success.main}, ${tokens.colors.accent.emerald})`}
          >
            <StatIcon $bg={tokens.colors.success.light}>💰</StatIcon>
            <StatLabel>Total Revenue</StatLabel>
            <StatValue>GHS {stats?.totalRevenue?.toFixed(2) || '0.00'}</StatValue>
            <StatLink to="/payouts">
              View payouts <ArrowRightIcon />
            </StatLink>
          </StatCard>
        </StatsGrid>

        <ContentGrid>

          <Card>
            <CardHeader>
              <h2>🚩 Patients Flagged for Follow-up</h2>
              <ViewAllLink to="/results">
                View Results <ArrowRightIcon />
              </ViewAllLink>
            </CardHeader>
            <CardBody>
              {followUpCandidates.length > 0 ? (
                <FollowUpList>
                  {followUpCandidates.slice(0, 5).map(r => (
                    <FollowUpItem key={r.id} to="/results">
                      <div>
                        <div className="client">{r.Client?.firstName} {r.Client?.lastName}</div>
                        <div className="meta">
                          {r.TestCatalog?.shortName} • {r.Practitioner ? `${r.Practitioner.firstName} ${r.Practitioner.lastName}` : 'Admin'} • {new Date(r.completedAt).toLocaleDateString()}
                        </div>
                      </div>
                      <RiskBadge level={r.followUpRisk?.level}>{r.followUpRisk?.level}</RiskBadge>
                      <div className="reason">{r.followUpRisk?.reasons?.[0] || r.followUpRisk?.recommendedAction}</div>
                    </FollowUpItem>
                  ))}
                </FollowUpList>
              ) : (
                <EmptyState>
                  <div className="icon">✅</div>
                  <p>No high-risk follow-up flags in the latest results scan</p>
                </EmptyState>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <h2>👥 Pending Practitioner Approvals</h2>
              <ViewAllLink to="/practitioners?status=pending">
                View All <ArrowRightIcon />
              </ViewAllLink>
            </CardHeader>
            <CardBody>
              {pendingPractitioners.length > 0 ? (
                <Table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>License</th>
                      <th>Applied</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingPractitioners.slice(0, 5).map(p => (
                      <tr key={p.id}>
                        <td>
                          <UserCell>
                            <span className="name">{p.firstName} {p.lastName}</span>
                            <span className="email">{p.email}</span>
                          </UserCell>
                        </td>
                        <td>{getLicenseTypeDisplay(p)}</td>
                        <td>{new Date(p.createdAt).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <EmptyState>
                  <div className="icon">✅</div>
                  <p>No pending approvals</p>
                </EmptyState>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <h2>📋 Recent Test Results</h2>
              <ViewAllLink to="/results">
                View All <ArrowRightIcon />
              </ViewAllLink>
            </CardHeader>
            <CardBody>
              {recentResults.length > 0 ? (
                <Table>
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th>Test</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentResults.slice(0, 5).map(r => (
                      <tr key={r.id}>
                        <td>{r.Client?.firstName} {r.Client?.lastName}</td>
                        <td>{r.TestCatalog?.shortName}</td>
                        <td>{new Date(r.completedAt).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <EmptyState>
                  <div className="icon">📭</div>
                  <p>No results yet</p>
                </EmptyState>
              )}
            </CardBody>
          </Card>
        </ContentGrid>
      </Container>
    </PageWrapper>
  );
};

export default Dashboard;
